import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';

const EMPTY = {
  displayName: '',
  contactEmail: '',
  brandPrimaryColor: '',
  brandSecondaryColor: '',
  brandLogoUrl: '',
  brandLoginTagline: '',
  allowedEmailDomains: [],
  requireVteAssessment: false,
};

const DOMAIN_PATTERN = /^[a-z0-9.-]+\.[a-z]{2,}$/;

const TenantBrandingSettings = ({ token, onSaved }) => {
  const [draft, setDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getTenantSettings(token);
        if (cancelled) return;
        const t = result.tenant || EMPTY;
        setDraft({
          displayName: t.displayName || '',
          contactEmail: t.contactEmail || '',
          brandPrimaryColor: t.brandPrimaryColor || '',
          brandSecondaryColor: t.brandSecondaryColor || '',
          brandLogoUrl: t.brandLogoUrl || '',
          brandLoginTagline: t.brandLoginTagline || '',
          allowedEmailDomains: Array.isArray(t.allowedEmailDomains) ? t.allowedEmailDomains : [],
          requireVteAssessment: Boolean(t.requireVteAssessment),
        });
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load tenant settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!d) return;
    if (!DOMAIN_PATTERN.test(d)) { setError(`"${d}" doesn't look like a valid domain (e.g. liverpool.ac.uk).`); return; }
    if ((draft.allowedEmailDomains || []).includes(d)) { setDomainInput(''); return; }
    setDraft({ ...draft, allowedEmailDomains: [...(draft.allowedEmailDomains || []), d] });
    setDomainInput('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await api.updateTenantSettings(draft, token);
      setMessage('Saved. Branding applied across the app.');
      if (onSaved) onSaved(result.tenant);
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>;
  }

  return (
    <div className="app-panel p-4">
      <h2 className="h5 mb-3">Tenant settings</h2>
      <p className="text-muted">
        These settings affect your whole institution. Display name and login tagline appear on
        the login page; primary colour skins buttons and links across the app.
      </p>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {message ? <Alert variant="success">{message}</Alert> : null}

      <Form.Group className="mb-3">
        <Form.Label>Display name</Form.Label>
        <Form.Control
          value={draft.displayName}
          onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Contact email</Form.Label>
        <Form.Control
          type="email"
          value={draft.contactEmail}
          onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value })}
        />
      </Form.Group>

      <hr className="my-4" />

      <h3 className="h6 mb-3">Branding</h3>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Primary colour</Form.Label>
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="color"
                value={draft.brandPrimaryColor || '#0d6efd'}
                onChange={(e) => setDraft({ ...draft, brandPrimaryColor: e.target.value })}
                style={{ width: 64, padding: 4 }}
              />
              <Form.Control
                value={draft.brandPrimaryColor}
                onChange={(e) => setDraft({ ...draft, brandPrimaryColor: e.target.value })}
                placeholder="#0d6efd"
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Secondary colour</Form.Label>
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="color"
                value={draft.brandSecondaryColor || '#6c757d'}
                onChange={(e) => setDraft({ ...draft, brandSecondaryColor: e.target.value })}
                style={{ width: 64, padding: 4 }}
              />
              <Form.Control
                value={draft.brandSecondaryColor}
                onChange={(e) => setDraft({ ...draft, brandSecondaryColor: e.target.value })}
                placeholder="#6c757d"
              />
            </div>
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>Logo URL</Form.Label>
        <Form.Control
          value={draft.brandLogoUrl}
          onChange={(e) => setDraft({ ...draft, brandLogoUrl: e.target.value })}
          placeholder="https://example.com/logo.png"
        />
        <Form.Text className="text-muted">PNG/SVG. Renders next to the brand name in the navbar.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Login tagline</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={draft.brandLoginTagline}
          onChange={(e) => setDraft({ ...draft, brandLoginTagline: e.target.value })}
          placeholder="Shown above the login form"
        />
      </Form.Group>

      <hr className="my-4" />

      <h3 className="h6 mb-1">Access control</h3>
      <p className="text-muted small mb-3">
        Restrict registration to specific institutional email domains. Leave empty to allow any email address.
      </p>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {(draft.allowedEmailDomains || []).map((domain) => (
          <Badge
            key={domain}
            bg="secondary"
            className="d-flex align-items-center gap-1 fs-6 fw-normal py-1 px-2"
            style={{ cursor: 'default' }}
          >
            {domain}
            <span
              role="button"
              aria-label={`Remove ${domain}`}
              style={{ cursor: 'pointer', marginLeft: 4 }}
              onClick={() => setDraft({ ...draft, allowedEmailDomains: draft.allowedEmailDomains.filter((d) => d !== domain) })}
            >
              ×
            </span>
          </Badge>
        ))}
        {(draft.allowedEmailDomains || []).length === 0 ? (
          <span className="text-muted small">No restrictions — any email accepted.</span>
        ) : null}
      </div>
      <div className="d-flex gap-2">
        <Form.Control
          size="sm"
          style={{ maxWidth: 240 }}
          value={domainInput}
          onChange={(e) => setDomainInput(e.target.value.toLowerCase().replace(/^@/, '').replace(/\s/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addDomain(); }
          }}
          placeholder="e.g. liverpool.ac.uk"
        />
        <Button size="sm" variant="outline-secondary" onClick={addDomain}>Add</Button>
      </div>

      <hr className="my-4" />
      <h3 className="h6 mb-1">Prescribing rules</h3>
      <p className="text-muted small mb-3">
        Configure clinical safety requirements for the ePMA simulator.
      </p>
      <Form.Check
        type="switch"
        id="require-vte"
        label="Require VTE assessment before prescribing"
        checked={draft.requireVteAssessment}
        onChange={(e) => setDraft({ ...draft, requireVteAssessment: e.target.checked })}
        className="mb-3"
      />

      <hr className="my-4" />

      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
};

export default TenantBrandingSettings;
